import { useDialogFocus } from '../hooks/useDialogFocus';
import type { Song } from '../data/mockCatalog';
import type { PurchaseState } from '../store/drumAppAtoms';

const COPY = {
  ko: {
    badge: 'GOOGLE PLAY',
    title: '이 난이도는 드럼하이 패스가 필요합니다',
    description: '드럼하이 패스로 공개된 모든 곡과 난이도를 자유롭게 연주할 수 있습니다.',
    passName: '드럼하이 패스',
    price: '월 9,900원',
    renewal: 'Google Play에서 매월 자동 갱신됩니다. 언제든 구독을 해지할 수 있습니다.',
    purchase: '드럼하이 패스 구매',
    chooseAnother: '다른 난이도 선택',
    restore: '구매 복원',
    checkingTitle: '상품 정보를 확인하고 있습니다',
    checkingDescription: 'Google Play에서 드럼하이 패스 정보를 불러오는 중입니다.',
    processingTitle: '결제를 처리하고 있습니다',
    processingDescription: '결제가 완료될 때까지 화면을 닫지 마세요.',
    successTitle: '드럼하이 패스 구매가 완료되었습니다',
    successDescription: '잠금이 해제되었습니다. 선택한 난이도를 바로 연주할 수 있습니다.',
    successAction: '연주 화면으로 돌아가기',
    cancelledTitle: '구매가 취소되었습니다',
    cancelledDescription: '결제되지 않았습니다. 원할 때 다시 구매할 수 있습니다.',
    failedTitle: '결제를 완료하지 못했습니다',
    failedDescription: 'Google Play 연결 상태를 확인한 후 다시 시도해 주세요.',
    retry: '다시 시도',
    restoredTitle: '구매 복원이 완료되었습니다',
    restoredDescription: '기존 드럼하이 패스 구매 내역을 확인하고 잠금을 해제했습니다.',
    restoredAction: '확인',
    close: '닫기',
  },
  en: {
    badge: 'GOOGLE PLAY',
    title: 'This difficulty requires DRUM Hi Pass',
    description: 'Play every difficulty across all published songs with DRUM Hi Pass.',
    passName: 'DRUM Hi Pass',
    price: '$7.99 / month',
    renewal: 'Renews automatically each month through Google Play. Cancel anytime.',
    purchase: 'Get DRUM Hi Pass',
    chooseAnother: 'Choose another difficulty',
    restore: 'Restore purchase',
    checkingTitle: 'Checking product information',
    checkingDescription: 'Loading DRUM Hi Pass details from Google Play.',
    processingTitle: 'Processing your purchase',
    processingDescription: 'Please keep this screen open until payment is complete.',
    successTitle: 'DRUM Hi Pass purchase complete',
    successDescription: 'This difficulty is now unlocked and ready to play.',
    successAction: 'Return to song',
    cancelledTitle: 'Purchase cancelled',
    cancelledDescription: 'You were not charged. You can try again whenever you are ready.',
    failedTitle: 'Purchase could not be completed',
    failedDescription: 'Check your Google Play connection and try again.',
    retry: 'Try again',
    restoredTitle: 'Purchase restored',
    restoredDescription: 'Your previous DRUM Hi Pass purchase was found and access is unlocked.',
    restoredAction: 'Continue',
    close: 'Close',
  },
} as const;

const STATE_CONTENT: Record<string, [keyof typeof COPY['en'], keyof typeof COPY['en']]> = {
  checking: ['checkingTitle', 'checkingDescription'],
  processing: ['processingTitle', 'processingDescription'],
  success: ['successTitle', 'successDescription'],
  cancelled: ['cancelledTitle', 'cancelledDescription'],
  failed: ['failedTitle', 'failedDescription'],
  restored: ['restoredTitle', 'restoredDescription'],
};

interface PurchaseGateProps {
  song: Song | null | undefined;
  difficulty: string;
  language?: string;
  state?: PurchaseState;
  onPurchase: () => void;
  onRestore: () => void;
  onCancel: (reason: 'close' | 'choose' | 'done') => void;
  onRetry: () => void;
}

export default function PurchaseGate({ song, difficulty, language = 'en', state = 'idle', onPurchase, onRestore, onCancel, onRetry }: PurchaseGateProps) {
  const copy = COPY[language === 'ko' ? 'ko' : 'en'];
  const currentState = STATE_CONTENT[state] ? state : 'idle';
  const stateContent = STATE_CONTENT[currentState];
  const title = stateContent ? copy[stateContent[0]] : copy.title;
  const description = stateContent ? copy[stateContent[1]] : copy.description;
  const songTitle = song?.title || '';
  const difficultyLabel = difficulty || '';
  const isBusy = currentState === 'checking' || currentState === 'processing';
  const dialogRef = useDialogFocus(true, isBusy ? null : () => onCancel('close'));

  return (
    <div className="purchase-gate-backdrop">
      <section
        ref={dialogRef as React.RefObject<HTMLElement>}
        className={`purchase-gate purchase-gate--${currentState}`}
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="purchase-gate-title"
        aria-describedby="purchase-gate-description"
        aria-busy={isBusy}
      >
        <header className="purchase-gate__header">
          <span className="purchase-gate__store">{copy.badge}</span>
          {!isBusy && (
            <button className="purchase-gate__close" type="button" onClick={() => onCancel('close')} aria-label={copy.close}>
              <span aria-hidden="true">×</span>
            </button>
          )}
        </header>

        <div className="purchase-gate__status" aria-live="assertive" aria-atomic="true">
          {isBusy && <span className="purchase-gate__spinner" aria-hidden="true" />}
          <h2 id="purchase-gate-title">{title}</h2>
          <p id="purchase-gate-description">{description}</p>
        </div>

        {(songTitle || difficultyLabel) && (
          <dl className="purchase-gate__selection">
            {songTitle && (<div><dt>{language === 'ko' ? '곡' : 'Song'}</dt><dd>{songTitle}</dd></div>)}
            {difficultyLabel && (<div><dt>{language === 'ko' ? '난이도' : 'Difficulty'}</dt><dd>{difficultyLabel}</dd></div>)}
          </dl>
        )}

        {(currentState === 'idle' || currentState === 'checking') && (
          <div className="purchase-gate__product">
            <div><strong>{copy.passName}</strong><span>{copy.price}</span></div>
            <p>{copy.renewal}</p>
          </div>
        )}

        <footer className="purchase-gate__actions">
          {currentState === 'idle' && (
            <>
              <button className="purchase-gate__primary" type="button" onClick={onPurchase}>{copy.purchase}</button>
              <button className="purchase-gate__secondary" type="button" onClick={() => onCancel('choose')}>{copy.chooseAnother}</button>
              <button className="purchase-gate__restore" type="button" onClick={onRestore}>{copy.restore}</button>
            </>
          )}

          {isBusy && (
            <button className="purchase-gate__secondary" type="button" disabled>
              {currentState === 'checking' ? copy.checkingTitle : copy.processingTitle}
            </button>
          )}

          {(currentState === 'failed' || currentState === 'cancelled') && (
            <>
              <button className="purchase-gate__primary" type="button" onClick={onRetry}>{copy.retry}</button>
              <button className="purchase-gate__secondary" type="button" onClick={() => onCancel('choose')}>{copy.chooseAnother}</button>
              <button className="purchase-gate__restore" type="button" onClick={onRestore}>{copy.restore}</button>
            </>
          )}

          {currentState === 'success' && (
            <button className="purchase-gate__primary" type="button" onClick={() => onCancel('done')}>{copy.successAction}</button>
          )}

          {currentState === 'restored' && (
            <button className="purchase-gate__primary" type="button" onClick={() => onCancel('done')}>{copy.restoredAction}</button>
          )}
        </footer>
      </section>
    </div>
  );
}
